import camelize from "camelize";

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  errors?: Record<string, string[]>;
  message?: string;
}

// Default request timeout, applied to every call that doesn't pass its own
// timeoutMs. Its job is to reap a request stuck on a dead/half-open socket
// (after the laptop sleeps, the network changes, or a proxy silently drops the
// connection) so it can't pin a browser connection-pool slot forever and stall
// every later request to the origin. Deliberately generous: it must never fire
// for a healthy-but-slow request, only for one that is genuinely hung.
export const DEFAULT_TIMEOUT_MS = 30_000;

// For calls that can legitimately run longer than the default (large binary
// downloads). Pass it explicitly via the timeoutMs option.
export const LONG_TIMEOUT_MS = 120_000;

export async function fetchWithCsrf<T>(
  url: string,
  options: RequestInit = {},
  {
    camelize: shouldCamelize = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }: { camelize?: boolean; timeoutMs?: number } = {},
): Promise<T> {
  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )?.content;

  const defaultHeaders =
    options.body instanceof FormData
      ? { "X-CSRF-Token": csrfToken || "" }
      : {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        };

  // Without a timeout a request that opens a connection but never sends a
  // response leaves the promise pending forever. timeoutMs defaults to
  // DEFAULT_TIMEOUT_MS, so an AbortController aborts the fetch (and any stalled
  // body read) and the request rejects and can be retried instead of hanging
  // indefinitely. The abort is composed with any signal the caller already
  // passed. A non-positive timeoutMs opts out ("no timeout") rather than
  // aborting immediately.
  const controller =
    timeoutMs != null && timeoutMs > 0 ? new AbortController() : undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const forwardAbort = () =>
    controller?.abort((options.signal as AbortSignal | undefined)?.reason);

  if (controller) {
    if (options.signal) {
      if (options.signal.aborted) forwardAbort();
      else
        options.signal.addEventListener("abort", forwardAbort, { once: true });
    }
    timeoutId = setTimeout(
      () =>
        controller.abort(new DOMException("Request timed out", "TimeoutError")),
      timeoutMs,
    );
  }

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller ? controller.signal : options.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    const textResponse = await response.text();
    let data: ApiResponse;

    try {
      data = textResponse ? JSON.parse(textResponse) : {};
    } catch {
      throw new Error(`Failed to parse JSON response: ${textResponse}`);
    }

    const result = shouldCamelize ? camelize(data) : data;

    if (!response.ok) {
      const error =
        typeof result === "object" && result !== null
          ? { ...(result as object), status: response.status }
          : { status: response.status };
      throw error;
    }

    return result as T;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    options.signal?.removeEventListener("abort", forwardAbort);
  }
}
